import { autoinject, bindable } from 'aurelia-framework';
import { IForm, State } from 'app/shared/lib/essette-site';
import { AccountService } from 'app/core/services/account-service';
import { LookupService } from 'app/core/services/lookup-service';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { AppSession } from 'app/shared/lib/essette-api';
import { ISessionUser } from 'app/shared/lib/session/session-types';
import { confirmation, ConfirmationOptions } from 'app/shared/elements/ess-confirm';
import { I18N } from 'aurelia-i18n';
import { Toaster } from 'app/shared/lib/toaster';
import { validatePhoneNumber } from 'app/shared/lib/validation/validation-utilities';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class EditAccount implements IForm {

    @bindable
    protected account: Core.AccountContext = {};

    protected first = '';
    protected last = '';
    protected department = '';
    protected title = '';
    protected email = '';
    protected phone = '';
    protected cell = '';
    protected fax = '';
    protected timeZone: Core.TimeZone | null = null;
    protected homePage: Core.AccountHomePage | null = null;

    protected timeZones: Core.TimeZone[] = [];
    protected homePages: Core.AccountHomePage[] = [];
    public readonly state: State = new State();

    protected validatePhoneNumber = validatePhoneNumber;

    constructor(
        private readonly env: EnvironmentService,
        private readonly i18n: I18N,
        private readonly log: AppLogger,
        private readonly session: AppSession,
        private readonly accountService: AccountService,
        private readonly lookupService: LookupService,
        private readonly toaster: Toaster) {
    }

    protected created(): void {
        this.initialize();
        this.load();
    }

    protected accountChanged(): void {
        this.initialize();
    }

    private async load(): Promise<void> {
        await this.loadLookups();
        if(this.account.id) {
            this.timeZone = this.account?.user?.displayTimeZone || {};
            this.homePage = this.account.homePage || null;
        }
    }

    private initialize(): void {
        if(this.account?.id) {
            this.first = this.account?.user?.name?.first || '';
            this.last = this.account?.user?.name?.last || '';
            this.department = this.account?.user?.department || '';
            this.title = this.account?.user?.title || '';
            this.email = this.account?.user?.emailAddress || '';
            this.phone = this.account?.user?.phoneNumber?.primary?.number || '';
            this.cell = this.account?.user?.phoneNumber?.cell?.number || '';
            this.fax = this.account?.user?.phoneNumber?.fax?.number || '';
            this.timeZone = this.account?.user?.displayTimeZone || {};
            this.homePage = this.account.homePage || null;
        }
    }

    public clear(): void {
        this.first = ''
        this.last = '';
        this.department
        this.title = ''
        this.email = ''
        this.phone = ''
        this.cell = '';
        this.fax = '';
        this.homePage = {};
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        return false;
    }
    public submittedChanges?: any;

    public async submit(): Promise<boolean> {
        if(!this.account || !this.account.id) return false;

        const changeTimeZone = this.timeZone?.code !== this.env.displayTimeZone;

        const saveAccount: Core.AccountContext = {
            id: this.account.id,
            homePage: {
                id: this.homePage?.id
            },
            user: {
                name: {
                    first: this.first,
                    last: this.last
                },
                emailAddress: this.email,
                displayTimeZone: {
                    id: this.timeZone?.id
                },
                title: this.title,
                department: this.department,
                phoneNumber: {
                    primary: {
                        number: this.phone || ''
                    },
                    cell: {
                        number: this.cell || ''
                    },
                    fax: {
                        number: this.fax || ''
                    }
                }
            }
        };

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.accountService.updateAccountContext(saveAccount);

            const updatedContext = (response.result as Core.AccountContext);
            console.log({ updatedContext });

            const { user, homePage } = updatedContext;
            if(response.isSuccessful && user) {
                isSuccessful = response.isSuccessful;

                const sessionUser: ISessionUser = {
                    ...this.env.user, ...{
                        title: user.title || '',
                        department: user.department || '',
                        description: user.description || '',
                        emailAddress: user.emailAddress || '',
                        name: {
                            first: user.name?.first || '',
                            middle: user.name?.middle || '',
                            last: user.name?.last || '',
                            display: user.name?.display || '',
                            full: user.name?.full || ''
                        },
                        timeZone: user.displayTimeZone?.code || '',
                        homePage: {
                            id: homePage?.id || '',
                            code: homePage?.code || '',
                            description: homePage?.description || '',
                            href: homePage?.href || ''
                        }
                    }
                };

                // update user saved in session
                this.session.updateUser(sessionUser);
                // persist in local storage, and notify other windows/tabs
                this.session.save('account');
                // update environment immediately
                this.env.updateSession({ user: sessionUser });

                // show a modal if Transitional is enabled
                if(changeTimeZone && this.env.config.essetteTransitional.enabled) {
                    const title = this.i18n.tr('core:messages.timeZone.change');
                    const message = this.i18n.tr('core:messages.timeZone.changeNotice');
                    confirmation.showCustom(
                        title,
                        message,
                        ConfirmationOptions.OK
                    );
                }
            }

            return response.isSuccessful;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
            if(isSuccessful) {
                this.toaster.info().message(this.i18n.tr('ui:messages.events.updated', { what: '$t(core:account.$)' })).show();
            }
        }
    }

    public reset?(): void {
        this.clear();
    }

    private async loadLookups(): Promise<void> {
        try {
            this.state.beginLoad();

            const [timeZones, homePages] = await Promise.all([
                this.lookupService.loadTimeZones({ all: true }),
                this.accountService.loadHomePages()
            ]);
            if(timeZones.isSuccessful) {
                this.timeZones = timeZones.results;
            }
            if(homePages.isSuccessful) {
                this.homePages = homePages.results;
            }
        }
        catch(error) {
            this.log.error('Error Loading Account Lookups', error);
        }
        finally {
            this.state.endLoad();
        }
    }

}
